import { BasePlugin, type IBaseConfig, type IPlugin } from "@easepick/base-plugin";
import { AmpPlugin, DateTime } from "@easepick/bundle";

declare module "@easepick/core/dist/types" {
    interface IPickerConfig {
        KeepSelectedDatePlugin?: IBaseConfig;
    }
}

export class KeepSelectedDatePlugin extends BasePlugin implements IPlugin {
    SELECTORS = {
        DROPDOWNS: ".month-name--select",
        MONTH_SELECT: "month-name--dropdown"
    };

    selectedDate: DateTime | null = null;
    public ampPlugin: AmpPlugin;
    private readonly viewHandler: (event: CustomEvent) => void;
    private readonly dropdownChangeHandler: (event: Event) => void;

    constructor() {
        super();
        this.viewHandler = this.handleView.bind(this);
        this.dropdownChangeHandler = this.handleDropdownChange.bind(this);
    }

    getName(): string {
        return "KeepSelectedDatePlugin";
    }

    onAttach(): void {
        this.picker.on("view", this.viewHandler);
    }

    onDetach(): void {
        this.picker.off("view", this.viewHandler);
    }

    private handleView = (e: CustomEvent) => {
        this.ampPlugin = this.picker.PluginManager.getInstance("AmpPlugin") as AmpPlugin;
        const { view, target } = e.detail;
        const monthNameSelects = target.querySelectorAll(this.SELECTORS.DROPDOWNS);

        if (this.picker.datePicked.length) {
            this.selectedDate = this.picker.datePicked[0];
        }

        if (view === "CalendarHeader" && monthNameSelects.length > 0) {
            monthNameSelects.forEach((element: HTMLSelectElement) => {
                element.addEventListener("change", this.dropdownChangeHandler);
            });
        }
    };

    private handleDropdownChange = (event: Event) => {
        const target = event.target as HTMLSelectElement;

        if (target.classList.contains(this.SELECTORS.MONTH_SELECT)) {
            const newMonth = parseInt(target.value, 10) + 1;
            this.updateDatePreservingDay(undefined, newMonth);
        } else {
            const newYear = parseInt(target.value, 10);
            this.updateDatePreservingDay(newYear);
        }

        if (this.selectedDate) {
            this.picker.datePicked = [this.selectedDate];
            this.picker.trigger("preselect", { date: this.selectedDate });
            this.picker.renderAll();
        }
    };

    private updateDatePreservingDay(newYear?: number, newMonth?: number, newDay?: number): void {
        if (!this.selectedDate) {
            return;
        }

        const day = newDay ?? this.selectedDate.getDate();
        const year = newYear ?? this.selectedDate.getFullYear();
        const month = newMonth ?? this.selectedDate.getMonth() + 1;

        const lastDayOfMonth = new Date(year, month, 0).getDate();
        const validDay = day <= lastDayOfMonth ? day : lastDayOfMonth;
        const selectedDate = new Date(year, month - 1, validDay);

        this.selectedDate = new DateTime(selectedDate);
    }
}
